import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../firebase';
import { doc, setDoc, getDocs, query, collection, where } from 'firebase/firestore';

// Custom styled components (assuming these are defined elsewhere or inline as before)
const StyledCard = ({ children }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '400px'
  }}>{children}</div>
);

const StyledInput = ({ label, type = 'text', name, value, onChange, error }) => ( // Added name prop
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ display: 'block', marginBottom: '0.5rem' }}>{label}</label>
    <input
      type={type}
      name={name} // Added name attribute
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: '0.75rem',
        border: `1px solid ${error ? 'red' : '#ccc'}`,
        borderRadius: '4px',
        fontSize: '1rem'
      }}
    />
    {error && <span style={{ color: 'red', fontSize: '0.875rem' }}>{error}</span>}
  </div>
);

const StyledSelect = ({ label, name, value, onChange, options }) => ( // Added name prop
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ display: 'block', marginBottom: '0.5rem' }}>{label}</label>
    <select
      name={name} // Added name attribute
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: '0.75rem',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '1rem',
        backgroundColor: 'white'
      }}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const StyledButton = ({ children, onClick, disabled, type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      width: '100%',
      padding: '0.75rem',
      backgroundColor: '#1976d2',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '1rem',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.7 : 1
    }}
  >
    {children}
  </button>
);

const StyledAlert = ({ type, children }) => (
  <div style={{
    padding: '0.75rem',
    marginBottom: '1rem',
    borderRadius: '4px',
    backgroundColor: type === 'error' ? '#fdeded' : '#edf7ed',
    color: type === 'error' ? '#5f2120' : '#1e4620',
    border: `1px solid ${type === 'error' ? '#ef5350' : '#4caf50'}`
  }}>
    {children}
  </div>
);

const LoadingSpinner = () => (
  <div style={{
    display: 'inline-block',
    width: '1.5rem',
    height: '1.5rem',
    border: '3px solid rgba(255,255,255,0.3)',
    borderRadius: '50%',
    borderTopColor: 'white',
    animation: 'spin 1s linear infinite'
  }}></div>
);

// Added nim and programStudi to initialForm
const initialForm = {
  email: '',
  password: '',
  confirmPassword: '',
  role: 'mahasiswa',
  nama: '', // Added full name
  nim: '',
  programStudi: '',
  nip: '', // Added NIP field
};

const RegisterPage = () => {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    // Ensure name attribute exists on inputs/selects
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Basic validation for student/lecturer fields
    if (form.role === 'mahasiswa' && (!form.nama || !form.nim || !form.programStudi)) {
      setError('Nama Lengkap, NIM, dan Program Studi wajib diisi untuk mahasiswa.');
      return;
    }
    if (form.role === 'dosen' && (!form.nama || !form.nip)) { // Added validation for dosen
      setError('Nama Lengkap dan NIP wajib diisi untuk dosen.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Password tidak cocok');
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
      // Simpan data user ke Firestore
      let approved = false;
      if (form.role === 'admin') {
        // Cek apakah sudah ada admin di Firestore
        const adminQuery = await getDocs(query(collection(db, 'users'), where('role', '==', 'admin')));
        if (adminQuery.empty) {
          approved = true; // Admin pertama, langsung approve
        }
      }

      // Prepare user data
      const userData = {
        email: form.email,
        role: form.role,
        approved,
        nama: form.nama, // Add name to user data
      };

      // Add student-specific fields if role is mahasiswa
      // Add role-specific fields
      if (form.role === 'mahasiswa') {
        userData.nim = form.nim;
        userData.programStudi = form.programStudi;
      } else if (form.role === 'dosen') { // Add NIP for dosen
        userData.nip = form.nip;
      }

      await setDoc(doc(db, 'users', userCredential.user.uid), userData); // Save userData

      setSuccess('Registrasi berhasil! Akun Anda menunggu persetujuan admin.');
      setForm(initialForm); // Reset form
    } catch (err) {
      // Provide more specific error messages if possible
      if (err.code === 'auth/email-already-in-use') {
        setError('Email sudah terdaftar. Silakan gunakan email lain.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password terlalu lemah. Gunakan minimal 6 karakter.');
      } else {
        setError('Registrasi gagal: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#f5f5f5',
      padding: '1rem'
    }}>
      <StyledCard>
        <h1 style={{
          fontSize: '1.5rem',
          textAlign: 'center',
          marginBottom: '1.5rem',
          color: '#1976d2'
        }}>Register</h1>

        {error && <StyledAlert type="error">{error}</StyledAlert>}
        {success && <StyledAlert type="success">{success}</StyledAlert>} {/* Show success message */}

        <form onSubmit={handleSubmit}>
          <StyledInput
            label="Email"
            type="email"
            name="email" // Added name
            value={form.email}
            onChange={handleChange}
            error={error && error.includes('Email') ? error : ''} // More specific error display
          />

          <StyledInput
            label="Password"
            type="password"
            name="password" // Added name
            value={form.password}
            onChange={handleChange}
            error={error && (error.includes('Password') || error.includes('password')) ? error : ''}
          />

          <StyledInput
            label="Konfirmasi Password"
            type="password"
            name="confirmPassword" // Added name
            value={form.confirmPassword}
            onChange={handleChange}
            error={error && error.includes('cocok') ? error : ''}
          />

          {/* Added Name Input */}
          <StyledInput
            label="Nama Lengkap"
            type="text"
            name="nama"
            value={form.nama}
            onChange={handleChange}
            error={error && error.includes('Nama') ? error : ''}
            required // Make name required
          />

          <StyledSelect
            label="Role"
            name="role" // Added name
            value={form.role}
            onChange={handleChange}
            options={[
              { value: 'mahasiswa', label: 'Mahasiswa' },
              { value: 'dosen', label: 'Dosen' }, // Added Dosen role
              { value: 'laboran', label: 'Laboran' },
              { value: 'admin', label: 'Admin' }
            ]}
          />

          {/* Conditional Inputs for Mahasiswa */}
          {form.role === 'mahasiswa' && (
            <>
              <StyledInput
                label="NIM"
                type="text"
                name="nim" // Added name
                value={form.nim}
                onChange={handleChange}
                error={error && error.includes('NIM') ? error : ''}
              />
              <StyledInput
                label="Program Studi"
                type="text"
                name="programStudi" // Added name
                value={form.programStudi}
                onChange={handleChange}
                error={error && error.includes('Program Studi') ? error : ''}
              />
            </>
          )}

          {/* Conditional Input for Dosen */}
          {form.role === 'dosen' && (
            <StyledInput
              label="NIP"
              type="text"
              name="nip" // Added name
              value={form.nip}
              onChange={handleChange}
              error={error && error.includes('NIP') ? error : ''}
            />
          )}

          <StyledButton type="submit" disabled={loading}>
            {loading ? <LoadingSpinner /> : 'Register'}
          </StyledButton>
        </form>

        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <a
            href="/login"
            style={{
              color: '#1976d2',
              textDecoration: 'none'
            }}
          >Sudah punya akun? Login</a>
        </div>
      </StyledCard>
    </div>
  );
};

export default RegisterPage;
